'use client';

import { useMemo, useState } from 'react';
import { Button } from '../../components/button';
import { ConfirmationDialog } from '../confirmation-dialog/ConfirmationDialog';
import { SettingsEmptyState } from '../settings/SettingsEmptyState';
import { IconButton } from '../../components/icon-button';
import { TableGrid, type TableGridRow } from '../table-grid/TableGrid';
import { Tooltip } from '../../components/tooltip';
import { ContentCard } from '../content-card/ContentCard';
import { Box, HStack } from '@terra/ui-styled-system';
import { Delete01Icon, FileSearchIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { AssetValidationResult } from '@terra/shared-utils';
import { automationId } from '@terra/shared-utils';
import { SurfaceScopeAddDialog } from './SurfaceScopeAddDialog';

type SurfaceScopeAddDialogCopy = {
  title: string;
  description: string;
  label: string;
  placeholder: string;
  helperText: string;
};

export type SurfaceScopeListContentProps = {
  values: string[];
  onChange: (values: string[]) => void;
  validator: (value: string) => AssetValidationResult;
  addDialog: SurfaceScopeAddDialogCopy;
  valueColumnTitle: string;
  searchPlaceholder: string;
  emptyStateTitle: string;
  emptyStateDescription?: string;
  duplicateErrorMessage: string;
  disabled?: boolean;
  tableMaxHeight?: string;
  embedded?: boolean;
  addDialogOpen?: boolean;
  onAddDialogOpenChange?: (open: boolean) => void;
  addDialogInputTestId?: string;
  addDialogConfirmTestId?: string;
};
export function SurfaceScopeListContent({
  values,
  onChange,
  validator,
  addDialog,
  valueColumnTitle,
  searchPlaceholder,
  emptyStateTitle,
  emptyStateDescription,
  duplicateErrorMessage,
  disabled = false,
  tableMaxHeight = '300px',
  embedded = false,
  addDialogOpen: controlledAddDialogOpen,
  onAddDialogOpenChange,
  addDialogInputTestId,
  addDialogConfirmTestId,
}: SurfaceScopeListContentProps) {
  const t = useTranslations('designSystem.surfaceScope');
  const tCommon = useTranslations('designSystem.common');
  const [internalAddDialogOpen, setInternalAddDialogOpen] = useState(false);
  const [valueToRemove, setValueToRemove] = useState<string | null>(null);
  const isAddDialogControlled = controlledAddDialogOpen !== undefined;
  const addDialogOpen = isAddDialogControlled
    ? controlledAddDialogOpen
    : internalAddDialogOpen;
  const setAddDialogOpen = (open: boolean) => {
    if (!isAddDialogControlled) {
      setInternalAddDialogOpen(open);
    }

    onAddDialogOpenChange?.(open);
  };

  const rows = useMemo<TableGridRow[]>(
    () =>
      values.map((value) => ({
        id: value,
        value,
        cells: [
          <Tooltip key={`${value}-label`} title={value}>
            <Box truncate maxW="full">
              {value}
            </Box>
          </Tooltip>,
          <HStack
            key={`${value}-actions`}
            gap={2}
            justifyContent="flex-end"
            alignItems="center"
            height="full"
          >
            <IconButton
              variant="ghost"
              size="xs"
              aria-label={tCommon('delete')}
              onClick={() => setValueToRemove(value)}
              disabled={disabled}
            >
              <Delete01Icon size={16} />
            </IconButton>
          </HStack>,
        ],
      })),
    [disabled, tCommon, values],
  );
  const handleAdd = (newValues: string[]) => {
    onChange([...values, ...newValues]);
  };

  const handleRemove = () => {
    if (!valueToRemove) {
      return;
    }

    onChange(values.filter((entry) => entry !== valueToRemove));
    setValueToRemove(null);
  };

  const emptyState = (
    <SettingsEmptyState
      title={emptyStateTitle}
      icon={<FileSearchIcon size={56} />}
    >
      {emptyStateDescription}
    </SettingsEmptyState>
  );
  const tableGrid = (
    <TableGrid
      columns={[
        {
          id: 'value',
          title: valueColumnTitle,
          width: 'minmax(0, 1fr)',
        },
        {
          id: 'actions',
          title: t('actionsColumn'),
          width: '80px',
        },
      ]}
      rows={rows}
      placeholder={searchPlaceholder}
      tableMaxHeight={tableMaxHeight}
      emptyState={emptyState}
      rightContent={
        embedded && !disabled && !isAddDialogControlled ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setAddDialogOpen(true)}
          >
            {tCommon('add')}
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <>
      {tableGrid}

      <SurfaceScopeAddDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onAdd={handleAdd}
        title={addDialog.title}
        description={addDialog.description}
        label={addDialog.label}
        placeholder={addDialog.placeholder}
        helperText={addDialog.helperText}
        validator={validator}
        duplicateErrorMessage={duplicateErrorMessage}
        existingValues={values}
        inputTestId={addDialogInputTestId}
        confirmTestId={addDialogConfirmTestId}
      />

      <ConfirmationDialog
        maxWidth="500px"
        isOpen={valueToRemove !== null}
        onOpenChange={({ open }) => {
          if (!open) {
            setValueToRemove(null);
          }
        }}
        onConfirm={handleRemove}
        variant="destructive"
        title={t('removeDialog.title')}
        description={t('removeDialog.description')}
        confirmText={t('removeDialog.confirm')}
        cancelText={tCommon('cancel')}
      />
    </>
  );
}

export type SurfaceScopeListCardProps = Omit<
  SurfaceScopeListContentProps,
  'embedded' | 'addDialogOpen' | 'onAddDialogOpenChange'
> & {
  title: string;
  description?: string;
  addButtonLabel: string;
  addButtonTestId?: string;
};

export function SurfaceScopeListCard({
  title,
  description,
  addButtonLabel,
  addButtonTestId,
  tableMaxHeight = '300px',
  disabled = false,
  ...contentProps
}: SurfaceScopeListCardProps) {
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  return (
    <ContentCard
      minHeight="340px"
      title={`${title} (${contentProps.values.length})`}
      description={description}
      rightSlot={
        disabled ? undefined : (
          <Button
            size="xs"
            variant="outline"
            onClick={() => setAddDialogOpen(true)}
            {...(addButtonTestId ? automationId(addButtonTestId) : {})}
          >
            {addButtonLabel}
          </Button>
        )
      }
    >
      <SurfaceScopeListContent
        {...contentProps}
        disabled={disabled}
        tableMaxHeight={tableMaxHeight}
        addDialogOpen={addDialogOpen}
        onAddDialogOpenChange={setAddDialogOpen}
      />
    </ContentCard>
  );
}
